import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { Route, Routes, Navigate } from 'react-router-dom';
import { Button, LinkButton } from 'components/buttons';
import { UserAccess, UserHasAccess } from 'components/user-access';
import { PageRestricted } from 'pages/errors/page-restricted';
import { EmployeeReviewsOverview } from 'pages/employee-reviews/overview';
import { EmployeeReviewsPending } from 'pages/employee-reviews/pending';
import { EmployeeReviewEdit, EmployeeReviewsCompleted, EmployeeReviewView } from 'pages/employee-reviews/completed';
import { EmployeeReviewComplete } from 'pages/employee-reviews/complete';
import { ReviewTypesListing, ReviewTypeCreate, ReviewTypeEdit } from 'pages/employee-reviews/review-types';
import { NewReviewModal } from 'pages/employee-reviews/new-review-modal';
import { Layout } from 'pages/employee-reviews/Layout';

const viewAction = "EmployeeReview:View";
const manageAction = "EmployeeReview:Manage";
const performAction = "EmployeeReview:Perform";
const overviewPath = "/employee-reviews/overview";
const reviewTypesPath = "/employee-reviews/review-types";

const homeBreadcrumb = { location: "/home", title: "HOME" };
const reviewTypesBreadcrumb = { location: reviewTypesPath, title: "REVIEW TYPES" };
const createBreadcrumbs = [homeBreadcrumb, reviewTypesBreadcrumb, { title: "New Review Type" }];
const editBreadcrumbs = [homeBreadcrumb, reviewTypesBreadcrumb, { title: "Edit Review Type" }];
const completedReviewBreadcrumbs = [homeBreadcrumb, { location: overviewPath, title: "Employee Reviews" }, { title: "Completed Review" }];
// The edit page adds the crumbs leading back to the review it edits, which only it knows.
const editReviewBreadcrumbs = [homeBreadcrumb, { location: overviewPath, title: "Employee Reviews" }];
const completeReviewBreadcrumbs = [homeBreadcrumb, { location: overviewPath, title: "Employee Reviews" }, { title: "Complete Review" }];

const newReviewTypeButton = (
    <LinkButton intent="primary" icon="plus" text="New Review Type" href={`${reviewTypesPath}/create`} id="new-review-type" />
);

export function EmployeeReviews(props) {

    // View users land on the overview; users who only hold Manage go straight to
    // the review-types admin listing, the only tab they can access.
    const hasViewAccess = UserHasAccess(viewAction);
    const hasPerformAccess = UserHasAccess(performAction);
    const defaultPath = hasViewAccess ? overviewPath : reviewTypesPath;

    const [newReviewModalOpen, setNewReviewModalOpen] = useState(false);

    // Layout renders its action row for any non-null button, so a user who cannot raise a review
    // must be given nothing at all rather than an element that renders empty.
    let newReviewButton = null;

    if (hasPerformAccess) {
        newReviewButton = (
            <Button intent="primary" icon="plus" text="New Review" id="new-review" onClick={() => setNewReviewModalOpen(true)} />
        );
    }

    function renderReviewTab(content) {
        return (
            <UserAccess perform={viewAction}
                yes={() => (
                    <Layout breadcrumbs={props.breadcrumbs} actionButton={newReviewButton}>
                        {content}
                    </Layout>
                )}
                no={() => (
                    <PageRestricted />
                )}
            />
        );
    }

    function renderViewContent(content) {
        return (
            <UserAccess perform={viewAction}
                yes={() => content}
                no={() => (
                    <PageRestricted />
                )}
            />
        );
    }

    function renderManageContent(content) {
        return (
            <UserAccess perform={manageAction}
                yes={() => content}
                no={() => (
                    <PageRestricted />
                )}
            />
        );
    }

    return (
        <Fragment>
            <Routes>
                {/* The review type editor pages own their page chrome, so they render outside the hub's tabbed layout. */}
                <Route path="review-types/create" element={renderManageContent(<ReviewTypeCreate breadcrumbs={createBreadcrumbs} />)} />
                <Route path="review-types/:reviewTypeId/*" element={renderManageContent(<ReviewTypeEdit breadcrumbs={editBreadcrumbs} />)} />
                <Route path="review-types" element={renderManageContent((
                    <Layout breadcrumbs={props.breadcrumbs} actionButton={newReviewTypeButton}>
                        <ReviewTypesListing />
                    </Layout>
                ))} />
                <Route path="overview" element={renderReviewTab(<EmployeeReviewsOverview />)} />
                <Route path="pending-reviews" element={renderReviewTab(<EmployeeReviewsPending />)} />
                {/* The complete-review wizard owns its page chrome and gates on its own action
                    (EmployeeReview:Perform, not View), so it renders outside the hub's tabbed layout
                    and outside the View-gated renderViewContent wrapper. */}
                <Route path="pending-reviews/:pendingReviewId/complete" element={<EmployeeReviewComplete breadcrumbs={completeReviewBreadcrumbs} />} />
                {/* A review raised for a driver is keyed on the employee and review type rather than on a
                    pending review, and is completed through the same wizard. */}
                <Route path="employees/:employeeId/review-types/:reviewTypeId/complete" element={<EmployeeReviewComplete breadcrumbs={completeReviewBreadcrumbs} />} />
                {/* The completed review record owns its page chrome, so it renders outside the hub's tabbed layout. */}
                <Route path="completed-reviews/:reviewId" element={renderViewContent(<EmployeeReviewView breadcrumbs={completedReviewBreadcrumbs} />)} />
                {/* Editing a completed review gates itself on both EmployeeReview:View and EmployeeReview:Perform, so it renders outside the View-only renderViewContent wrapper. */}
                <Route path="completed-reviews/:reviewId/edit" element={<EmployeeReviewEdit breadcrumbs={editReviewBreadcrumbs} />} />
                <Route path="completed-reviews" element={renderReviewTab(<EmployeeReviewsCompleted />)} />
                <Route path="" element={<Navigate to={defaultPath} replace />} />
            </Routes>

            {/* One instance for the whole hub: every review tab's button opens the same modal. */}
            <NewReviewModal isOpen={newReviewModalOpen} onClose={() => setNewReviewModalOpen(false)} />
        </Fragment>
    );
}

EmployeeReviews.propTypes = {
    breadcrumbs: PropTypes.array
};
